/**
 * The library rules the Görsel Kütüphanesi screen needs and `lib/reference.ts` does not carry.
 *
 * `m01b-assets.js` is where the prototype keeps them: content-addressed identity
 * (`asset_<sha256[0..24]>`), the ingestion gate with its own Turkish refusals, the
 * `AssetLibrary.usedBy` scan over template drafts and releases, and the project export/import
 * envelope from `m08c-library.js`. They are ported here rather than into `reference.ts` because
 * that file is shared with every other screen being rebuilt in parallel.
 *
 * The seed rows come from `data/demo-assets.json` through `lib/render/library.ts` — the same
 * library the render plan resolves against, so the grid and the rendered credentials cannot
 * disagree about what an asset is.
 */
import type { DemoDatabase, DemoTemplate } from '../data/types';
import { allAssets, allFonts } from './render/library';

export const ASSET_LIMITS = { maxBytes: 15 * 1024 * 1024, minPx: 64, maxPx: 8000 } as const;
export const FONT_LIMITS = { maxBytes: 6 * 1024 * 1024 } as const;
export const PROJECT_SCHEMA_NAME = 'hpw-credentials-project';
export const PROJECT_SCHEMA_VERSION = '0.7.0';
export const IMPORT_LIMITS = {
  maxBytes: 64 * 1024 * 1024,
  maxAssets: 400,
  maxFonts: 40,
  maxTemplates: 400,
  maxReleases: 2000,
  maxCredentials: 5000,
  maxBatches: 500,
} as const;

/** `settings` is replaced field by field on import, never merged wholesale (`m08c-library.js`). */
const IMPORT_SETTINGS_WHITELIST = [
  'issuerDisplayName',
  'issuerWebsite',
  'linkedinPageUrl',
  'issuerProfileId',
  'publicBase',
  'supportEmail',
] as const;

const MIME_EXT: Record<string, string[]> = {
  'image/png': ['png'],
  'image/jpeg': ['jpg', 'jpeg'],
  'image/svg+xml': ['svg'],
};

export interface LibraryAsset {
  id: string;
  sha256: string;
  mimeType: string;
  byteSize: number;
  width: number;
  height: number;
  aspectRatio: number;
  /**
   * Whether the drawing must composite this image over white — NOT a claim about the file.
   *
   * Where the pixels were examined (`ingestImageFile`, and the reference's own dump) this is what was
   * found. Where they were not, it is the cautious default for a format that CAN carry alpha, which is
   * the right thing to DRAW with and the wrong thing to TELL somebody: a PNG with no transparent pixel
   * in it would be labelled «şeffaf» on the strength of its media type alone. `alphaKnown` is what
   * separates the two, and only a known value may be shown to a person.
   */
  hasAlpha: boolean;
  /** True only when the pixels were actually examined. Absent means they were not. */
  alphaKnown?: boolean;
  originalFileName: string;
  dataUrlForPrototype: string;
}

export type FaceState = 'loaded' | 'failed' | 'pending';

export interface LibraryFont {
  id: string;
  sha256: string;
  family: string;
  weight: number;
  style: string;
  byteSize: number;
  licenseAcknowledged: boolean;
  state: FaceState;
}

/**
 * The two seeded faces' byte counts, from `public/v07/fonts/FONTS.json`.
 *
 * `demo-assets.json` drops `byteSize` for fonts — nothing in the render path reads it — but the
 * library table shows it, and 157/152 KB are what the reference's own rows say.
 */
const FONT_BYTE_SIZE: Record<string, number> = {
  '7e65201e9b79159e2300267cc885e16c8dcef2424cdfa09a29bfb0980a94a7ba': 160316,
  '983676516167748b74de6f4771fb384c664fd913acb8b471122ecacf5da5ea6c': 155996,
};

/**
 * A listed asset, as the library model.
 *
 * Every field is the SERVER's — its id, its digest, its dimensions, taken after it sanitised the SVG
 * or stripped the PNG's chunks. A digest the browser computed before the upload is a different digest
 * whenever that rewriting changed a byte, and storing one as a reference would bind a drawing to bytes
 * the server never had.
 *
 * `hasAlpha` is the one field the list projection does not carry — the backend stores no such column
 * and nothing in the upload path measures it. It decides whether a background is composited over
 * white, so the default for an image whose format CAN carry transparency is that it might; a JPEG
 * cannot. That default is a DRAWING decision and `alphaKnown` stays false, so no screen presents it
 * as something anybody measured.
 */
export function libraryAssetFromApi(
  row: {
    id: string;
    sha256: string;
    mediaType: string;
    byteSize: number;
    width: number;
    height: number;
    originalFilename: string | null;
  },
  contentUrl: string,
): LibraryAsset {
  return {
    id: row.id,
    sha256: row.sha256,
    mimeType: row.mediaType,
    byteSize: row.byteSize,
    width: row.width,
    height: row.height,
    aspectRatio: row.height === 0 ? 1 : Number((row.width / row.height).toFixed(6)),
    hasAlpha: row.mediaType !== 'image/jpeg',
    alphaKnown: false,
    originalFileName: row.originalFilename ?? row.id,
    dataUrlForPrototype: contentUrl,
  };
}

/** A listed face. `state` is what the browser reported, never an assumption. */
export function libraryFontFromApi(
  row: { id: string; sha256: string; family: string; weight: number; style: string; byteSize: number },
  state: FaceState,
): LibraryFont {
  return {
    id: row.id,
    sha256: row.sha256,
    family: row.family,
    weight: row.weight,
    style: row.style === 'italic' ? 'italic' : 'normal',
    byteSize: row.byteSize,
    /* The server only stores a face whose licence was acknowledged; the column is its timestamp. */
    licenseAcknowledged: true,
    state,
  };
}

export const assetIdFor = (sha: string): string => `asset_${sha.slice(0, 24)}`;
export const fontIdFor = (sha: string): string => `font_${sha.slice(0, 24)}`;

/**
 * The transparency line — drawn only where somebody actually looked.
 *
 * One definition for the three places that print it (the library grid's tile, the upload result line
 * and the editor's background chip), because the whole point is that they agree. `hasAlpha` is a
 * drawing decision; saying « · şeffaf» on the strength of it turned that decision into a claim about
 * the operator's own file. The decision is unchanged — only what is said about it.
 */
export const alphaNote = (a: { hasAlpha: boolean; alphaKnown?: boolean }): string =>
  a.alphaKnown === true ? (a.hasAlpha ? ' · şeffaf' : '') : ' · şeffaflık bilinmiyor';

/**
 * The demo's own library.
 *
 * `demo-assets.json` is the reference's library exactly as the running reference held it, so its
 * `hasAlpha` is what the reference's own `detectAlpha` found in those pixels — measured, not assumed.
 */
export const seedAssets = (): LibraryAsset[] => allAssets().map((a) => ({ ...a, alphaKnown: true }));

/**
 * Both seeded faces are registered by `styles/fonts.css` with the reference's own bytes, so their
 * `faceState` is `loaded` on this surface for the same reason it is in the prototype — the face is
 * actually there. An uploaded face gets its state from a real `FontFace.load()` below.
 */
export const seedFonts = (): LibraryFont[] =>
  allFonts().map((f) => ({
    ...f,
    byteSize: FONT_BYTE_SIZE[f.sha256] ?? 0,
    licenseAcknowledged: true,
    state: 'loaded' as FaceState,
  }));

/** Templates that reference an asset in their draft or in any release (`AssetLibrary.usedBy`). */
export function assetUsedBy(assetId: string, templates: DemoTemplate[]): DemoTemplate[] {
  const inLayout = (layout: unknown): boolean => {
    if (!layout || typeof layout !== 'object') return false;
    const l = layout as { background?: { assetRef?: { assetId?: string } } | null; layers?: unknown[] };
    if (l.background?.assetRef?.assetId === assetId) return true;
    return (l.layers ?? []).some(
      (layer) => (layer as { assetRef?: { assetId?: string } })?.assetRef?.assetId === assetId,
    );
  };
  return templates.filter((t) => inLayout(t.draft) || (t.releases ?? []).some(inLayout));
}

// ------------------------------------------------------------------ bytes, digests and ingestion

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('Bu tarayıcıda SHA-256 hesaplanamıyor (güvenli bağlam gerekir).');
  const view = new Uint8Array(bytes);
  const digest = await subtle.digest('SHA-256', view.buffer as ArrayBuffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function sniffImageBytes(u8: Uint8Array): string | null {
  if (u8[0] === 0x89 && u8[1] === 0x50 && u8[2] === 0x4e && u8[3] === 0x47) return 'image/png';
  if (u8[0] === 0xff && u8[1] === 0xd8 && u8[2] === 0xff) return 'image/jpeg';
  const head = new TextDecoder().decode(u8.subarray(0, 400)).trim();
  if (/^<\?xml/.test(head) || /<svg[\s>]/i.test(head)) return 'image/svg+xml';
  return null;
}

function sniffFontBytes(u8: Uint8Array): string | null {
  const tag = String.fromCharCode(u8[0] ?? 0, u8[1] ?? 0, u8[2] ?? 0, u8[3] ?? 0);
  if (tag === 'wOFF') return 'font/woff';
  if (tag === 'wOF2') return 'font/woff2';
  if (tag === 'OTTO') return 'font/otf';
  if (u8[0] === 0x00 && u8[1] === 0x01 && u8[2] === 0x00 && u8[3] === 0x00) return 'font/ttf';
  if (tag === 'true' || tag === 'ttcf') return 'font/ttf';
  return null;
}

/** Chunked, because `String.fromCharCode(...bytes)` blows the argument limit on a real image. */
function bytesToDataUrl(u8: Uint8Array, mime: string): string {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < u8.length; i += chunk) {
    bin += String.fromCharCode(...u8.subarray(i, i + chunk));
  }
  return `data:${mime};base64,${btoa(bin)}`;
}

function dataUrlBytes(dataUrl: string): Uint8Array {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) throw new Error('data URL çözümlenemedi.');
  const body = dataUrl.slice(comma + 1);
  const bin = /;base64/i.test(dataUrl.slice(0, comma)) ? atob(body) : decodeURIComponent(body);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

const SVG_LENGTH_UNITS: Record<string, number> = {
  '': 1,
  px: 1,
  pt: 96 / 72,
  pc: 16,
  mm: 96 / 25.4,
  cm: 96 / 2.54,
  in: 96,
  q: 96 / 101.6,
};

/** `"210mm"` → pixels. A percentage or an em-relative length is not resolvable and returns null. */
function parseSvgLength(raw: string | null): number | null {
  if (raw === null) return null;
  const m = /^\s*([+-]?\d*\.?\d+(?:e[+-]?\d+)?)\s*([a-zA-Z%]*)\s*$/.exec(raw);
  if (!m) return null;
  const n = Number.parseFloat(m[1] ?? '');
  const unit = (m[2] ?? '').toLowerCase();
  if (!Number.isFinite(n) || n <= 0) return null;
  if (unit === '%') return null;
  const factor = SVG_LENGTH_UNITS[unit];
  if (factor === undefined) return null;
  return n * factor;
}

/**
 * Script, event handler or external reference in an uploaded SVG.
 *
 * The reference sanitises with an element/attribute allowlist and reports what it stripped. This
 * surface refuses the file instead of claiming to have cleaned it: sanitisation is a server-side
 * control in the product (it is implemented in the backend's ingestion pipeline), and a half
 * ported allowlist that silently passes something through is worse than a refusal.
 *
 * API GAP: asset.create — server-side SVG sanitisation, malware scan and durable object storage.
 */
const SVG_HOSTILE = /<\s*script|\son[a-z]+\s*=|javascript:|<\s*foreignObject|@import|<!ENTITY/i;
const SVG_EXTERNAL_URL = /url\(\s*['"]?\s*(?!data:image\/|#)[a-z]+:/i;

interface SvgDimensions {
  width: number;
  height: number;
}

/** Dimension resolution is fail-closed, as upstream: width/height → viewBox → refusal. */
function svgDimensions(text: string): SvgDimensions {
  if (SVG_HOSTILE.test(text) || SVG_EXTERNAL_URL.test(text)) {
    throw new Error('SVG betik, olay niteliği ya da dış kaynak taşıyor; temizleme sunucu tarafındadır.');
  }
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  if (doc.querySelector('parsererror')) throw new Error('SVG geçersiz (ayrıştırılamadı).');
  const root = doc.documentElement;
  if (root.nodeName.toLowerCase() !== 'svg') throw new Error('Kök element <svg> değil.');

  let w = parseSvgLength(root.getAttribute('width'));
  let h = parseSvgLength(root.getAttribute('height'));
  const viewBox = root.getAttribute('viewBox');
  let vb: number[] | null = null;
  if (viewBox) {
    const parts = viewBox
      .trim()
      .split(/[\s,]+/)
      .map(Number);
    if (
      parts.length === 4 &&
      parts.every((n) => Number.isFinite(n)) &&
      (parts[2] ?? 0) > 0 &&
      (parts[3] ?? 0) > 0
    ) {
      vb = parts;
    }
  }
  if (!(w && w > 0) || !(h && h > 0)) {
    if (!vb) throw new Error('SVG ölçüsü çözümlenemedi: geçerli width/height ya da viewBox yok.');
    const ratio = (vb[2] ?? 1) / (vb[3] ?? 1);
    if (w && w > 0) h = w / ratio;
    else if (h && h > 0) w = h * ratio;
    else {
      w = vb[2] ?? 0;
      h = vb[3] ?? 0;
    }
  }
  const width = Math.round(w ?? 0);
  const height = Math.round(h ?? 0);
  if (!(width > 0 && height > 0)) throw new Error('SVG ölçüsü geçersiz.');
  return { width, height };
}

function loadImageEl(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error('Görsel çözülemedi'));
    im.src = src;
  });
}

/** The reference's own probe: 96px wide sample, any pixel under alpha 250 makes it transparent. */
async function detectAlpha(dataUrl: string, w: number, h: number): Promise<boolean> {
  try {
    const im = await loadImageEl(dataUrl);
    const size = 96;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = Math.max(1, Math.round((size * h) / w));
    const g = canvas.getContext('2d', { willReadFrequently: true });
    if (!g) return false;
    g.clearRect(0, 0, canvas.width, canvas.height);
    g.drawImage(im, 0, 0, canvas.width, canvas.height);
    const data = g.getImageData(0, 0, canvas.width, canvas.height).data;
    for (let i = 3; i < data.length; i += 4) if ((data[i] ?? 255) < 250) return true;
    return false;
  } catch {
    return false;
  }
}

export interface AssetIngestResult {
  asset: LibraryAsset;
  deduped: boolean;
}

/**
 * Bytes → immutable asset, with the reference's refusals and its dedupe-by-digest rule: the same
 * content uploaded twice returns the existing record instead of opening a second one.
 */
export async function ingestImageFile(file: File, existing: LibraryAsset[]): Promise<AssetIngestResult> {
  const raw = new Uint8Array(await file.arrayBuffer());
  if (raw.length > ASSET_LIMITS.maxBytes) {
    throw new Error(
      `Dosya ${Math.round(ASSET_LIMITS.maxBytes / 1048576)} MB sınırını aşıyor (${(raw.length / 1048576).toFixed(1)} MB).`,
    );
  }
  const mime = sniffImageBytes(raw);
  if (!mime) throw new Error('Dosya PNG, JPG/JPEG veya SVG değil (içerik imzası tanınmadı).');
  const ext = (file.name.split('.').pop() ?? '').toLowerCase();
  const exts = MIME_EXT[mime] ?? [];
  if (ext && !exts.includes(ext)) throw new Error(`Uzantı (.${ext}) dosya içeriğiyle uyuşmuyor (${mime}).`);

  let width: number;
  let height: number;
  const dataUrl = bytesToDataUrl(raw, mime);
  if (mime === 'image/svg+xml') {
    const dim = svgDimensions(new TextDecoder().decode(raw));
    width = dim.width;
    height = dim.height;
  } else {
    const im = await loadImageEl(dataUrl);
    width = im.naturalWidth;
    height = im.naturalHeight;
  }
  if (width < ASSET_LIMITS.minPx || height < ASSET_LIMITS.minPx) {
    throw new Error(`Görsel çok küçük (${width}×${height}); en az ${ASSET_LIMITS.minPx} px kenar gerekir.`);
  }
  if (width > ASSET_LIMITS.maxPx || height > ASSET_LIMITS.maxPx) {
    throw new Error(
      `Görsel çok büyük (${width}×${height}); en çok ${ASSET_LIMITS.maxPx} px kenar kabul edilir.`,
    );
  }
  const sha256 = await sha256Hex(raw);
  const already = existing.find((a) => a.sha256 === sha256);
  if (already) return { asset: already, deduped: true };
  return {
    asset: {
      id: assetIdFor(sha256),
      sha256,
      mimeType: mime,
      byteSize: raw.length,
      width,
      height,
      aspectRatio: Number((width / height).toFixed(6)),
      hasAlpha: mime === 'image/jpeg' ? false : await detectAlpha(dataUrl, width, height),
      /* Examined: a JPEG cannot carry alpha, and everything else was decoded and sampled. */
      alphaKnown: true,
      originalFileName: file.name || `gorsel.${exts[0] ?? 'png'}`,
      dataUrlForPrototype: dataUrl,
    },
    deduped: false,
  };
}

export interface FontMeta {
  family: string;
  weight: number;
  style: string;
  licenseAcknowledged: boolean;
}

export interface FontIngestResult {
  font: LibraryFont;
  deduped: boolean;
}

/**
 * A real `FontFace.load()`, so a face's pill reports what actually happened.
 *
 * Exported because the product needs it for a face it did not just upload: the bytes come back from
 * the server (`asset.read`) when the library is read, and the face has to be registered with the
 * browser before the editor can measure or draw with it. Without that the browser substitutes another
 * typeface and nothing on screen can tell the substitute from the uploaded face — which is the one
 * thing a font picker must never be wrong about.
 */
export async function loadFontFace(
  family: string,
  bytes: ArrayBuffer | Uint8Array,
  meta: { weight: number; style: string },
): Promise<FaceState> {
  try {
    const buffer = bytes instanceof ArrayBuffer ? bytes : (new Uint8Array(bytes).buffer as ArrayBuffer);
    const face = new FontFace(family, buffer, {
      weight: String(meta.weight),
      style: meta.style,
    });
    await face.load();
    document.fonts.add(face);
    return 'loaded';
  } catch {
    return 'failed';
  }
}

const registerFace = (family: string, bytes: Uint8Array, meta: FontMeta): Promise<FaceState> =>
  loadFontFace(family, bytes, meta);

export async function ingestFontFile(
  file: File,
  meta: FontMeta,
  existing: LibraryFont[],
): Promise<FontIngestResult> {
  const raw = new Uint8Array(await file.arrayBuffer());
  if (raw.length > FONT_LIMITS.maxBytes) {
    throw new Error(`Font ${Math.round(FONT_LIMITS.maxBytes / 1048576)} MB sınırını aşıyor.`);
  }
  if (!sniffFontBytes(raw)) throw new Error('Dosya TTF/OTF/WOFF/WOFF2 imzası taşımıyor.');
  if (!meta.licenseAcknowledged) throw new Error('Font lisans onayı gerekli.');
  const sha256 = await sha256Hex(raw);
  const id = fontIdFor(sha256);
  const current = existing.find((f) => f.id === id);
  if (current) return { font: current, deduped: true };
  const family = meta.family.trim() || 'Özel Font';
  const state = await registerFace(family, raw, meta);
  return {
    font: {
      id,
      sha256,
      family,
      weight: meta.weight,
      style: meta.style === 'italic' ? 'italic' : 'normal',
      byteSize: raw.length,
      licenseAcknowledged: true,
      state,
    },
    deduped: false,
  };
}

/** The metadata modal's guesses from the file name (`openFontMetadataModal`). */
export function guessFontMeta(fileName: string): { family: string; weight: number; italic: boolean } {
  const base = fileName.replace(/\.[^.]+$/, '');
  const family =
    base
      .replace(/[-_ ]?(regular|semibold|bold|medium|light|extrabold|thin|black|italic)+$/i, '')
      .replace(/[-_]+/g, ' ')
      .trim() || 'Özel Font';
  const weight = /extrabold|black|800|900/i.test(base)
    ? 800
    : /semibold|600/i.test(base)
      ? 600
      : /bold|700/i.test(base)
        ? 700
        : /medium|500/i.test(base)
          ? 500
          : /light|300/i.test(base)
            ? 300
            : /thin|100|200/i.test(base)
              ? 200
              : 400;
  return { family, weight, italic: /italic|oblique/i.test(base) };
}

// ------------------------------------------------------------------------ applying to a template

const DRAFT_KEYS = [
  'credentialType',
  'customTypeLabel',
  'subjectType',
  'holderField',
  'defaultLanguage',
  'localizedMetadata',
  'validityPolicy',
  'publicationPolicy',
  'permissions',
  'duplicatePolicy',
  'emailDefaults',
  'interoperability',
  'canvas',
  'background',
  'fieldDefinitions',
  'layers',
  'safeAreas',
  'renderProfile',
] as const;

function makeCanvas(width: number, height: number): Record<string, unknown> {
  const w = Math.max(16, Math.round(width));
  const h = Math.max(16, Math.round(height));
  return {
    width: w,
    height: h,
    aspectRatio: Number((w / h).toFixed(6)),
    orientation: w === h ? 'square' : w > h ? 'landscape' : 'portrait',
    unit: 'px',
    outputScale: 1,
  };
}

function makeBackground(asset: LibraryAsset, fitMode: string): Record<string, unknown> {
  return {
    assetRef: { assetId: asset.id, sha256: asset.sha256 },
    hasAlpha: asset.hasAlpha,
    fitMode,
    x: 0.5,
    y: 0.5,
    scale: 1,
    crop: null,
    focal: null,
    rotation: 0,
    opacity: 1,
  };
}

/**
 * «Şablona uygula» writes a DRAFT and nothing else — the single publish gate is the editor
 * (`m08c-library.js` §13). The draft is taken from the current release when there is one, so
 * applying an image to a published template starts from what is published rather than from a
 * stale draft.
 */
export function applyAssetToDraft(
  template: DemoTemplate,
  asset: LibraryAsset,
  resizeCanvas: boolean,
  now: string,
): void {
  const release = template.currentReleaseId
    ? template.releases.find((r) => r.id === template.currentReleaseId)
    : undefined;
  const source = (release ?? template.draft) as unknown as Record<string, unknown>;
  const draft: Record<string, unknown> = {};
  for (const key of DRAFT_KEYS) {
    if (source[key] !== undefined) draft[key] = structuredClone(source[key]);
  }
  if (resizeCanvas) draft.canvas = makeCanvas(asset.width, asset.height);
  draft.background = makeBackground(asset, resizeCanvas ? 'native' : 'cover');
  draft.draftNote = `Görsel değiştirildi: ${asset.originalFileName || asset.id} (SHA-256 ${asset.sha256.slice(0, 12)}…)`;
  template.draft = draft as unknown as DemoTemplate['draft'];
  template.userModified = true;
  template.updatedAt = now;
}

// ------------------------------------------------------------------------------ project transfer

export function exportProject(
  db: DemoDatabase,
  assets: LibraryAsset[],
  fonts: LibraryFont[],
  appVersion: string,
): Record<string, unknown> {
  return {
    schemaName: PROJECT_SCHEMA_NAME,
    schemaVersion: PROJECT_SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    appVersion,
    assets: structuredClone(assets),
    fontAssets: structuredClone(fonts),
    templates: db.templates.map((t) => ({
      id: t.id,
      name: t.name,
      code: t.code,
      createdAt: t.createdAt,
      updatedAt: t.updatedAt,
      archivedAt: t.archivedAt ?? null,
      currentReleaseId: t.currentReleaseId,
      userModified: !!t.userModified,
      origin: t.origin,
    })),
    drafts: db.templates
      .filter((t) => t.draft)
      .map((t) => ({ templateId: t.id, draft: structuredClone(t.draft) })),
    releases: db.templates.flatMap((t) => (t.releases ?? []).map((r) => structuredClone(r))),
    subjects: structuredClone(db.subjects),
    /* Seed records are the reference's demo data, not the user's work, and stay out of the file. */
    credentials: db.credentials.filter((c) => c.origin !== 'seed').map((c) => structuredClone(c)),
    batches: db.batches.filter((b) => b.origin !== 'seed').map((b) => structuredClone(b)),
    settings: structuredClone(db.settings),
  };
}

export interface ImportPlan {
  ok: boolean;
  errors: string[];
  warnings: string[];
  notes: string[];
  assets: LibraryAsset[];
  fontAssets: LibraryFont[];
  templates: Record<string, unknown>[];
  drafts: { templateId: string; draft: Record<string, unknown> }[];
  releases: Record<string, unknown>[];
  subjects: Record<string, unknown>[];
  credentials: Record<string, unknown>[];
  batches: Record<string, unknown>[];
  settings: Record<string, unknown> | null;
  counts: Record<string, number>;
}

const asArray = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? (value.filter((x) => x && typeof x === 'object') as Record<string, unknown>[]) : [];

const str = (o: Record<string, unknown>, k: string): string =>
  typeof o[k] === 'string' ? (o[k] as string) : '';

/**
 * The dry run. Schema identity, size and record limits, and content-addressing are checked here,
 * against the real bytes: an asset whose `dataUrlForPrototype` no longer hashes to its `sha256`,
 * or that carries a second content under an id the library already holds, is refused.
 *
 * API GAP: project.import — the release-hash recomputation, field/visibility schema validation,
 * credential snapshot verification and the graph invariant pass (rule 9) are server-side and are
 * reported here as an unchecked note rather than silently assumed to have passed.
 */
export async function planProjectImport(
  parsed: unknown,
  byteSize: number,
  current: { assets: LibraryAsset[]; fonts: LibraryFont[] },
): Promise<ImportPlan> {
  const plan: ImportPlan = {
    ok: false,
    errors: [],
    warnings: [],
    notes: [],
    assets: [],
    fontAssets: [],
    templates: [],
    drafts: [],
    releases: [],
    subjects: [],
    credentials: [],
    batches: [],
    settings: null,
    counts: {},
  };
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    plan.errors.push('Dosya bir nesne değil.');
    return plan;
  }
  const p = parsed as Record<string, unknown>;
  if (p.schemaName !== PROJECT_SCHEMA_NAME) {
    plan.errors.push(
      `Bu dosya bir ${PROJECT_SCHEMA_NAME} aktarımı değil (schemaName: ${String(p.schemaName)}).`,
    );
    return plan;
  }
  const version = str(p, 'schemaVersion');
  if (!version) {
    plan.errors.push('schemaVersion eksik.');
    return plan;
  }
  if (version.split('.')[0] !== PROJECT_SCHEMA_VERSION.split('.')[0]) {
    plan.errors.push(
      `Şema sürümü uyumsuz: dosya ${version}, bu sürüm ${PROJECT_SCHEMA_VERSION}. Otomatik dönüştürme yapılmaz.`,
    );
    return plan;
  }
  if (byteSize > IMPORT_LIMITS.maxBytes) {
    plan.errors.push(
      `Dosya çok büyük (${(byteSize / 1048576).toFixed(1)} MB > ${IMPORT_LIMITS.maxBytes / 1048576} MB).`,
    );
    return plan;
  }

  const overflow: string[] = [];
  const limit = (key: string, max: number): void => {
    const n = asArray(p[key]).length;
    if (n > max) overflow.push(`${key}: ${n} > ${max}`);
  };
  limit('assets', IMPORT_LIMITS.maxAssets);
  limit('fontAssets', IMPORT_LIMITS.maxFonts);
  limit('templates', IMPORT_LIMITS.maxTemplates);
  limit('releases', IMPORT_LIMITS.maxReleases);
  limit('credentials', IMPORT_LIMITS.maxCredentials);
  limit('batches', IMPORT_LIMITS.maxBatches);
  if (overflow.length) {
    plan.errors.push(`Kayıt sayısı limitleri aşıldı — ${overflow.join(', ')}`);
    return plan;
  }

  for (const raw of asArray(p.assets)) {
    const label = str(raw, 'originalFileName') || str(raw, 'id') || '(isimsiz)';
    try {
      const sha = str(raw, 'sha256');
      const dataUrl = str(raw, 'dataUrlForPrototype');
      if (!sha || !dataUrl) throw new Error('digest ya da içerik taşımıyor');
      if (str(raw, 'id') !== assetIdFor(sha)) throw new Error('kimlik içerik adresli değil');
      const recomputed = await sha256Hex(dataUrlBytes(dataUrl));
      if (recomputed !== sha) {
        throw new Error(
          `digest eşleşmiyor (beklenen ${sha.slice(0, 12)}…, hesaplanan ${recomputed.slice(0, 12)}…)`,
        );
      }
      const held = current.assets.find((a) => a.id === raw.id);
      if (held && held.sha256 !== sha) {
        plan.errors.push(
          `Asset «${label}» aynı ID altında farklı içerik taşıyor; immutable asset overwrite edilemez (${String(raw.id)}).`,
        );
        continue;
      }
      if (held) {
        plan.notes.push(`Asset «${label}» zaten aynı digest ile kayıtlı — dedupe edildi.`);
        continue;
      }
      plan.assets.push(raw as unknown as LibraryAsset);
    } catch (e) {
      plan.errors.push(`Asset «${label}» reddedildi: ${(e as Error).message}`);
    }
  }

  for (const raw of asArray(p.fontAssets)) {
    const label = `${str(raw, 'family')} ${String(raw.weight ?? '')}`.trim() || '(isimsiz)';
    const sha = str(raw, 'sha256');
    if (!sha || str(raw, 'id') !== fontIdFor(sha)) {
      plan.errors.push(`Font «${label}» reddedildi: kimlik içerik adresli değil.`);
      continue;
    }
    const held = current.fonts.find((f) => f.id === raw.id);
    if (held && held.sha256 !== sha) {
      plan.errors.push(`Font «${label}» aynı ID altında farklı içerik taşıyor (${String(raw.id)}).`);
      continue;
    }
    if (held) {
      plan.notes.push(`Font «${label}» zaten kayıtlı — dedupe edildi.`);
      continue;
    }
    if (!raw.licenseAcknowledged) plan.warnings.push(`Font «${label}» lisans teyidi taşımıyor.`);
    plan.fontAssets.push(raw as unknown as LibraryFont);
  }

  for (const t of asArray(p.templates)) {
    if (!str(t, 'id') || !str(t, 'name')) {
      plan.errors.push('Şablon kaydı eksik alanlı.');
      continue;
    }
    plan.templates.push(t);
  }
  for (const d of asArray(p.drafts)) {
    const templateId = str(d, 'templateId');
    if (templateId && d.draft && typeof d.draft === 'object') {
      plan.drafts.push({ templateId, draft: d.draft as Record<string, unknown> });
    }
  }
  for (const r of asArray(p.releases)) {
    if (!str(r, 'id') || !str(r, 'templateId')) {
      plan.errors.push(`Release kaydı eksik alanlı (${str(r, 'id') || '—'}).`);
      continue;
    }
    plan.releases.push(r);
  }
  for (const s of asArray(p.subjects)) {
    if (str(s, 'id') && str(s, 'canonicalDisplayName')) plan.subjects.push(s);
  }
  for (const c of asArray(p.credentials)) {
    if (str(c, 'id') && str(c, 'publicCode')) plan.credentials.push(c);
    else plan.errors.push('Credential kaydı kimliksiz.');
  }
  for (const b of asArray(p.batches)) {
    if (str(b, 'id')) plan.batches.push(b);
  }
  if (p.settings && typeof p.settings === 'object') plan.settings = p.settings as Record<string, unknown>;

  plan.notes.push(
    'Release hash yeniden hesaplaması, alan/visibility şeması ve credential snapshot doğrulaması bu yüzeyde çalıştırılmaz; ürün sözleşmesinde sunucu tarafındadır.',
  );
  plan.counts = {
    assets: plan.assets.length,
    fontAssets: plan.fontAssets.length,
    templates: plan.templates.length,
    releases: plan.releases.length,
    credentials: plan.credentials.length,
    batches: plan.batches.length,
  };
  plan.ok = plan.errors.length === 0;
  return plan;
}

/** The whitelisted settings fields, replaced one by one — the block is never merged wholesale. */
export function applyImportedSettings(
  target: Record<string, unknown>,
  incoming: Record<string, unknown>,
): void {
  for (const key of IMPORT_SETTINGS_WHITELIST) {
    if (incoming[key] !== undefined) target[key] = incoming[key];
  }
}

// ------------------------------------------------------------------------------------ small bits

/** `copyText` in `m01-utils.js`: clipboard first, hidden textarea second, honest failure third. */
export async function copyText(text: string): Promise<boolean> {
  const fallback = (): boolean => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    ta.remove();
    return ok;
  };
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* falls through to the textarea */
  }
  return fallback();
}

/** The BOM is the reference's, so the exported JSON opens the same way in Excel and editors. */
export function downloadText(filename: string, text: string, mime = 'text/plain'): void {
  const a = document.createElement('a');
  a.href = `data:${mime};charset=utf-8,${encodeURIComponent(`﻿${text}`)}`;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
